import { z } from "zod";

export const companySchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(1, "Your name is required")
    .max(100, "Must be 100 characters or fewer"),
  company: z
    .string()
    .trim()
    .max(120, "Must be 120 characters or fewer")
    .optional()
    .or(z.literal("")),
  peLicense: z
    .string()
    .trim()
    .max(40, "Must be 40 characters or fewer")
    .optional()
    .or(z.literal("")),
  office: z
    .string()
    .trim()
    .max(80, "Must be 80 characters or fewer")
    .optional()
    .or(z.literal("")),
});

export type CompanyValues = z.infer<typeof companySchema>;

export const templateSchema = z.object({
  standard: z.enum(["e1527-21", "e1903-19", "aai"]),
  jurisdictionState: z
    .string()
    .trim()
    .max(2, "Use the 2-letter state code")
    .optional()
    .or(z.literal("")),
  additionalRequirements: z
    .string()
    .trim()
    .max(500, "Must be 500 characters or fewer")
    .optional()
    .or(z.literal("")),
});

export type TemplateValues = z.infer<typeof templateSchema>;

const ZIP_RE = /^\d{5}(-\d{4})?$/;

export const siteSchema = z
  .object({
    address: z.string().trim().max(200).optional().or(z.literal("")),
    city: z.string().trim().max(80).optional().or(z.literal("")),
    state: z.string().trim().max(2).optional().or(z.literal("")),
    zip: z
      .string()
      .trim()
      .optional()
      .or(z.literal(""))
      .refine((v) => !v || ZIP_RE.test(v), {
        message: "ZIP must be 5 digits (or 5+4)",
      }),
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
  })
  .refine(
    (v) => {
      // If any field is provided, address + city + state must all be set.
      const anyProvided = !!(v.address || v.city || v.state || v.zip);
      if (!anyProvided) return true;
      return !!v.address && !!v.city && !!v.state;
    },
    {
      message: "Address, city, and state are required for the first site",
      path: ["address"],
    },
  );

export type SiteValues = z.infer<typeof siteSchema>;
